import React from 'react'
import { openOHIFViewer } from '../../../utils/ohifViewer'
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material'
import {
  Visibility as ViewIcon,
  Description as ReportIcon,
  LocalHospital as StudyIcon,
  Attachment as DocumentIcon
} from '@mui/icons-material'
import { TimelineEntry as TimelineEntryType } from '../../../types/api'

interface TimelineEntryProps {
  entry: TimelineEntryType
  isLast?: boolean
  onViewStudy?: (viewerUrl: string) => void
  onViewReport?: (entry: TimelineEntryType) => void
}

/**
 * TimelineEntry - O singură intrare în timeline (COMPACT - single row)
 * 
 * Design compact:
 * - Tot pe un singur rând
 * - Dot mic, badges inline, butoane la final
 */
const TimelineEntry: React.FC<TimelineEntryProps> = ({ 
  entry, 
  isLast = false,
  onViewStudy,
  onViewReport 
}) => {
  const theme = useTheme()
  const { designSystem } = theme
  const colors = theme.palette.mode === 'dark' 
    ? designSystem.colors.dark 
    : designSystem.colors.light

  // Culoare dot pe baza statusului
  const getDotColor = () => {
    if (entry.status === 'FINALIZAT' || entry.status === 'final' || entry.status?.includes('finalizat')) {
      return designSystem.colors.status.success
    }
    if (entry.status === 'URGENT' || entry.status === 'urgent') {
      return designSystem.colors.status.error
    }
    if (entry.status === 'draft' || entry.status === 'DRAFT') {
      return designSystem.colors.status.warning
    }
    return colors.accent.primary
  }

  // Formatează data scurt
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  // Icon pe baza tipului
  const getTypeIcon = () => {
    switch (entry.type) {
      case 'study':
        return <StudyIcon sx={{ fontSize: 12 }} />
      case 'report':
        return <ReportIcon sx={{ fontSize: 12 }} />
      case 'document':
        return <DocumentIcon sx={{ fontSize: 12 }} />
      default:
        return <StudyIcon sx={{ fontSize: 12 }} />
    }
  }

  const dotColor = getDotColor()

  // Scurtează statusul
  const getShortStatus = (status: string | undefined) => {
    if (!status) return null
    if (status.toLowerCase().includes('finalizat')) return 'FINALIZAT'
    if (status.toLowerCase().includes('alocat')) return 'ALOCAT'
    return status.toUpperCase()
  }

  const shortStatus = getShortStatus(entry.status)

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        pl: 3,
        py: 0.75,
        borderBottom: isLast ? 'none' : `1px solid ${alpha(colors.border.default, 0.5)}`,
        '&:hover': {
          backgroundColor: alpha(colors.accent.primary, 0.04),
        }
      }}
    >
      {/* Vertical Line */}
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            left: 7,
            top: '50%',
            bottom: -12,
            width: 2,
            backgroundColor: alpha(colors.border.default, 0.5),
          }}
        />
      )}

      {/* Dot - mic */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: dotColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          boxShadow: `0 0 0 2px ${colors.background.paper}`,
          zIndex: 1,
        }}
      >
        {getTypeIcon()}
      </Box>

      {/* Content - Single Row */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minWidth: 0,
        }}
      >
        {/* Title - truncated */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
            fontSize: '0.8rem',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
          title={entry.title}
        >
          {entry.title}
        </Typography>

        {/* Date */}
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            fontSize: '0.7rem',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {formatDate(entry.date)}
        </Typography>

        {/* Badges - inline */}
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {shortStatus && (
            <Chip
              label={shortStatus}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 600,
                backgroundColor: alpha(dotColor, 0.15),
                color: dotColor,
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          )}

          {entry.comparison_status && entry.comparison_status !== 'INITIAL' && (
            <Chip
              label={entry.comparison_status}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 600,
                backgroundColor: alpha('#f97316', 0.15),
                color: '#f97316',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          )}

          {entry.modality && (
            <Chip
              label={entry.modality}
              size="small"
              variant="outlined"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 500,
                borderColor: alpha(colors.border.default, 0.5),
                color: colors.text.secondary,
                '& .MuiChip-label': { px: 0.5 }
              }}
            />
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
          {entry.viewer_url && (
            <Tooltip title="Deschide în viewer" arrow>
              <IconButton
                size="small"
                onClick={() => {
                  const viewerUrl = entry.viewer_url?.replace('/ohif/viewer', '/webviewer/viewer')
                  if (viewerUrl && onViewStudy) {
                    onViewStudy(viewerUrl)
                  } else if (viewerUrl) {
                    const match = viewerUrl.match(/StudyInstanceUIDs=([^&]+)/)
                    if (match && match[1]) {
                      void openOHIFViewer(match[1])
                    } else {
                      window.open(viewerUrl, '_blank')
                    }
                  }
                }}
                sx={{
                  p: 0.5,
                  backgroundColor: alpha(colors.accent.primary, 0.1),
                  color: colors.accent.primary,
                  '&:hover': {
                    backgroundColor: alpha(colors.accent.primary, 0.2),
                  }
                }}
              >
                <ViewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {(entry.report_id || entry.report_url) && (
            <Tooltip title="Vezi raportul" arrow>
              <IconButton
                size="small"
                onClick={() => onViewReport?.(entry)}
                sx={{
                  p: 0.5,
                  backgroundColor: alpha(designSystem.colors.status.success, 0.1),
                  color: designSystem.colors.status.success,
                  '&:hover': {
                    backgroundColor: alpha(designSystem.colors.status.success, 0.2),
                  }
                }}
              >
                <ReportIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default TimelineEntry
